import { useMemo, useState } from 'react';
import {
  Alert, AutoComplete, Button, Col, Divider, Input, InputNumber, Modal, Row, Segmented, Select, Space, Table, Tag, Tree, Typography, message,
} from 'antd';
import { DeleteOutlined, PlusOutlined, QuestionCircleOutlined, RobotOutlined, ThunderboltOutlined } from '@ant-design/icons';
import {
  P1_TEMPLATES,
  P2_TEMPLATES,
  inferFieldPaths,
  validateExpressionText,
  type ComputeRule,
  type ConditionGroup,
  type ConditionItem,
  type FieldRule,
  type TransformSpec,
  type TransformStep,
} from '@dtwin/shared';
import { generateParseScript, generateParseSpec } from '../../api/ai';
import AiRequirementHelpModal from './AiRequirementHelpModal';

const { Text } = Typography;

export interface VisualTransformEditorProps {
  mode: 'rows' | 'object';
  spec: TransformSpec;
  onChange: (spec: TransformSpec) => void;
  sample?: unknown;
  samplePaths?: string[];
  /** P2：显示输出路由（points/io/network） */
  showOutput?: boolean;
  /** P2：采用 AI 代码（切到代码模式并填入脚本） */
  onApplyCode?: (script: string) => void;
}

function getMapFields(spec: TransformSpec): FieldRule[] {
  return spec.steps.filter((s) => s.op === 'map').flatMap((s) => (s as any).fields as FieldRule[]);
}

function getComputeFields(spec: TransformSpec): ComputeRule[] {
  return spec.steps.filter((s) => s.op === 'compute').flatMap((s) => (s as any).fields as ComputeRule[]);
}

function getFilter(spec: TransformSpec): ConditionGroup | null {
  const step = spec.steps.find((s) => s.op === 'filter') as any;
  return step?.cond ?? null;
}

function rebuild(
  spec: TransformSpec,
  parts: { map?: FieldRule[]; filter?: ConditionGroup | null; compute?: ComputeRule[] },
): TransformSpec {
  const others = spec.steps.filter((s) => !['map', 'filter', 'compute'].includes(s.op));
  const map = parts.map ?? getMapFields(spec);
  const filter = parts.filter !== undefined ? parts.filter : getFilter(spec);
  const compute = parts.compute ?? getComputeFields(spec);
  const steps: TransformStep[] = [];
  if (map.length) steps.push({ op: 'map', fields: map });
  if (filter && filter.items?.length) steps.push({ op: 'filter', cond: filter });
  if (compute.length) steps.push({ op: 'compute', fields: compute });
  steps.push(...others);
  return { ...spec, steps };
}

function suggestName(path: string): string {
  const seg = path.replace(/\[(\d+)\]/g, '').split('.').filter(Boolean).pop() || 'field';
  const safe = seg.replace(/[^A-Za-z0-9_$]/g, '_');
  return /^[A-Za-z_$]/.test(safe) ? safe : `_${safe}`;
}

function parseScalar(raw: string): unknown {
  const s = raw.trim();
  if (s === '') return undefined;
  if (s === 'true') return true;
  if (s === 'false') return false;
  if (s === 'null') return null;
  const n = Number(s);
  return Number.isNaN(n) ? s : n;
}

function preview(v: unknown): string {
  if (typeof v === 'string') return v.length > 18 ? `${v.slice(0, 18)}…` : v;
  if (v === null) return 'null';
  if (typeof v === 'object') return Array.isArray(v) ? `[${v.length}]` : '{…}';
  return String(v);
}

function resolveSamplePath(value: unknown, path?: string): unknown {
  if (!path) return value;
  const segs = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
  let cur: any = value;
  for (const s of segs) {
    if (cur === null || cur === undefined) return undefined;
    cur = cur[s];
  }
  return cur;
}

/** P1（rows）用第一行做样例树/推断；P2 用整条报文 */
function sampleForEditor(mode: 'rows' | 'object', sample: unknown, path?: string): unknown {
  if (mode !== 'rows') return sample;
  const v = resolveSamplePath(sample, path);
  return Array.isArray(v) ? v[0] : v;
}

function buildTree(value: unknown, path: string, key: string, depth = 0): any[] {  if (depth > 5) return [];
  if (Array.isArray(value)) {
    return value.slice(0, 5).map((v, i) => {
      const p = `${path}[${i}]`;
      const children = buildTree(v, p, `${key}-${i}`, depth + 1);
      return {
        key: p,
        title: (
          <span>
            <Tag style={{ marginInlineEnd: 4 }}>[{i}]</Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>{preview(v)}</Text>
          </span>
        ),
        children,
        isLeaf: children.length === 0,
      };
    });
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).map(([k, v]) => {
      const p = path ? `${path}.${k}` : k;
      const children = buildTree(v, p, `${key}-${k}`, depth + 1);
      return {
        key: p,
        title: (
          <span>
            <Text>{k}</Text>
            {children.length === 0 && (
              <Text type="secondary" style={{ fontSize: 12, marginInlineStart: 6 }}>{preview(v)}</Text>
            )}
          </span>
        ),
        children,
        isLeaf: children.length === 0,
      };
    });
  }
  return [];
}

export default function VisualTransformEditor({
  mode,
  spec,
  onChange,
  sample,
  samplePaths,
  showOutput,
  onApplyCode,
}: VisualTransformEditorProps) {
  const mapFields = getMapFields(spec);
  const computeFields = getComputeFields(spec);
  const filter = getFilter(spec);
  const fields = useMemo(
    () => [...mapFields.map((f) => f.to).filter(Boolean), ...computeFields.map((f) => f.to).filter(Boolean)],
    [spec],
  );
  const visualSample = useMemo(
    () => sampleForEditor(mode, sample, spec.source?.path),
    [mode, sample, spec.source?.path],
  );
  const paths = useMemo(() => {
    if (samplePaths && samplePaths.length) return samplePaths;
    return inferFieldPaths(visualSample);
  }, [visualSample, samplePaths]);
  const treeData = useMemo(
    () => (visualSample === undefined ? [] : buildTree(visualSample, '', 'root')),
    [visualSample],
  );
  const templates = mode === 'rows' ? P1_TEMPLATES : P2_TEMPLATES;
  const [aiOpen, setAiOpen] = useState(false);
  const [aiHelpOpen, setAiHelpOpen] = useState(false);
  const [aiProduct, setAiProduct] = useState<'spec' | 'code'>('spec');
  const [aiNotes, setAiNotes] = useState('');
  const [aiTargets, setAiTargets] = useState('');
  const [aiProtocol, setAiProtocol] = useState('auto');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiResult, setAiResult] = useState<any>(null);

  const setMap = (next: FieldRule[]) => onChange(rebuild(spec, { map: next }));
  const setCompute = (next: ComputeRule[]) => onChange(rebuild(spec, { compute: next }));
  const setFilter = (next: ConditionGroup | null) => onChange(rebuild(spec, { filter: next }));

  const updateField = (index: number, patch: Partial<FieldRule>) => {
    const next = mapFields.map((f, i) => (i === index ? { ...f, ...patch } : f));
    setMap(next);
  };

  const addField = (from: string) => {
    if (!from) return;
    const name = suggestName(from);
    const existing = new Set(mapFields.map((f) => f.to));
    let to = name;
    let n = 2;
    while (existing.has(to)) to = `${name}${n++}`;
    setMap([...mapFields, { from, to, type: 'number' }]);
  };

  const infer = () => {
    const existing = new Set(mapFields.map((f) => f.from));
    const add: FieldRule[] = [];
    for (const p of paths) {
      if (add.length >= 12) break;
      if (existing.has(p)) continue;
      if (p.includes('[0].')) continue;
      existing.add(p);
      add.push({ from: p, to: suggestName(p), type: 'number' });
    }
    if (!add.length) return;
    const used = new Set(mapFields.map((f) => f.to));
    for (const f of add) {
      let to = f.to;
      let n = 2;
      while (used.has(to)) to = `${f.to}${n++}`;
      f.to = to;
      used.add(to);
    }
    setMap([...mapFields, ...add]);
  };

  const runAiGenerate = async () => {
    setAiLoading(true);
    setAiError('');
    setAiResult(null);
    try {
      const targets = aiTargets
        .split(/[,，\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (aiProduct === 'code' && onApplyCode) {
        const r = await generateParseScript({
          requirements: aiNotes,
          samples: sample === undefined ? undefined : [sample],
          targetFields: targets.length ? targets : undefined,
          protocol: aiProtocol === 'auto' ? undefined : aiProtocol,
          autoRepair: true,
          maxAttempts: 3,
        });
        setAiResult({ ...r, product: 'code' });
        if (!r.ok) setAiError((r.errors || ['生成失败']).join('；'));
      } else {
        const r = await generateParseSpec({
          sample,
          samples: sample === undefined ? undefined : [sample],
          mode,
          requirements: aiNotes,
          targetFields: targets.length ? targets : undefined,
          protocol: aiProtocol === 'auto' ? undefined : aiProtocol,
          autoRepair: true,
          maxAttempts: 3,
        });
        setAiResult({ ...r, product: 'spec' });
        if (!r.ok) setAiError((r.errors || ['生成失败']).join('；'));
      }
    } catch (e: any) {
      setAiError(e?.message || String(e));
    } finally {
      setAiLoading(false);
    }
  };

  const applyAi = () => {
    if (aiResult?.product === 'code') {
      if (!aiResult.script || !onApplyCode) return;
      onApplyCode(aiResult.script);
      message.success(`已采用 AI 代码（尝试 ${aiResult.attempts ?? 1} 次）`);
      setAiOpen(false);
      return;
    }
    if (!aiResult?.spec) return;
    onChange(aiResult.spec as TransformSpec);
    message.success(`已采用（尝试 ${aiResult.attempts ?? 1} 次）`);
    setAiOpen(false);
  };

  const columns: any[] = [
    {
      title: '源路径',
      dataIndex: 'from',
      width: 220,
      render: (_: unknown, f: FieldRule, i: number) => (
        <AutoComplete
          size="small"
          value={f.from}
          options={paths.map((p) => ({ value: p }))}
          onChange={(v) => updateField(i, { from: v })}
          placeholder="如 data.points.temp"
          data-testid="vt-map-from"
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '目标字段',
      dataIndex: 'to',
      width: 130,
      render: (_: unknown, f: FieldRule, i: number) => (
        <Input size="small" value={f.to} onChange={(e) => updateField(i, { to: e.target.value })} placeholder="temp" data-testid="vt-map-to" />
      ),
    },
    {
      title: '类型',
      dataIndex: 'type',
      width: 96,
      render: (_: unknown, f: FieldRule, i: number) => (
        <Select
          size="small"
          value={f.type ?? 'number'}
          style={{ width: '100%' }}
          onChange={(v) => updateField(i, { type: v })}
          data-testid="vt-map-type"
          options={[
            { value: 'number', label: '数字' },
            { value: 'string', label: '文本' },
            { value: 'boolean', label: '布尔' },
            { value: 'date', label: '日期' },
            { value: 'json', label: 'JSON' },
          ]}
        />
      ),
    },
    {
      title: '单位/换算',
      width: 120,
      render: (_: unknown, f: FieldRule, i: number) => (
        <Select
          size="small"
          value={f.unit?.kind ?? 'none'}
          style={{ width: '100%' }}
          onChange={(v) => {
            if (v === 'none') updateField(i, { unit: undefined });
            else updateField(i, { unit: { kind: v, scale: f.unit?.scale ?? 1, offset: f.unit?.offset ?? 0 } });
          }}
          options={[
            { value: 'none', label: '无' },
            { value: 'cToF', label: '℃→℉' },
            { value: 'fToC', label: '℉→℃' },
            { value: 'scale', label: '×倍数' },
          ]}
        />
      ),
    },
    {
      title: '小数位',
      width: 80,
      render: (_: unknown, f: FieldRule, i: number) => (
        <InputNumber
          size="small"
          min={0}
          max={6}
          value={f.decimals}
          onChange={(v) => updateField(i, { decimals: v === null ? undefined : v })}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '默认值',
      width: 100,
      render: (_: unknown, f: FieldRule, i: number) => (
        <Input
          size="small"
          value={f.fallback === undefined ? '' : String(f.fallback)}
          onChange={(e) => updateField(i, { fallback: parseScalar(e.target.value) })}
          placeholder="缺失时"
        />
      ),
    },
    {
      title: '',
      width: 40,
      render: (_: unknown, __: FieldRule, i: number) => (
        <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => setMap(mapFields.filter((_, x) => x !== i))} />
      ),
    },
  ];

  const filterItems = filter?.items ?? [];
  const updateFilterItem = (index: number, patch: Partial<ConditionItem>) => {
    const items = filterItems.map((it, i) => (i === index ? { ...(it as ConditionItem), ...patch } : it));
    setFilter({ logic: filter?.logic ?? 'and', items });
  };

  const outputValue = spec.output;
  const setOutput = (key: 'points' | 'io' | 'network', value: string[] | undefined) => {
    const next = { ...(spec.output ?? {}), [key]: value && value.length ? value : undefined };
    const hasAny = !!(next.points?.length || next.io?.length || next.network?.length);
    const copy = { ...spec };
    if (hasAny) copy.output = next;
    else delete copy.output;
    onChange(copy);
  };

  return (
    <div>
      <Space wrap size={8}>
        <Select
          size="small"
          placeholder="套用模板（推荐）"
          style={{ width: 230 }}
          value={undefined}
          onChange={(id) => {
            const t = templates.find((x) => x.id === id);
            if (t) onChange(JSON.parse(JSON.stringify(t.spec)));
          }}
          options={templates.map((t) => ({ value: t.id, label: `${t.name} · ${t.description}` }))}
          data-testid="vt-template"
        />
        <Button size="small" icon={<ThunderboltOutlined />} onClick={infer} data-testid="vt-infer">
          从样例推断字段
        </Button>
        <Button
          size="small"
          icon={<RobotOutlined />}
          disabled={sample === undefined}
          onClick={() => {
            setAiError('');
            setAiOpen(true);
          }}
          data-testid="vt-ai"
        >
          AI 生成
        </Button>
        <Text type="secondary" style={{ fontSize: 12 }}>
          点左侧样例节点可快速添加映射；目标字段 = 大屏/绑定使用的字段名
        </Text>
      </Space>

      {mode === 'rows' && (
        <Space size={6} style={{ marginTop: 6 }} wrap>
          <Text style={{ fontSize: 12 }}>数据路径</Text>
          <Input
            size="small"
            style={{ width: 220 }}
            value={spec.source?.path ?? ''}
            placeholder="留空=原始数组；如 data.items"
            onChange={(e) =>
              onChange({
                ...spec,
                source: e.target.value.trim() ? { path: e.target.value.trim() } : undefined,
              })
            }
            data-testid="vt-source-path"
          />
          <Text type="secondary" style={{ fontSize: 12 }}>指向数组的路径（映射字段按行取值）</Text>
        </Space>
      )}

      <Row gutter={12} style={{ marginTop: 8 }}>
        <Col span={7}>
          <Text strong style={{ fontSize: 12 }}>样例报文</Text>
          <div style={{ maxHeight: 320, overflow: 'auto', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, padding: 6, marginTop: 4 }} data-testid="vt-sample-tree">
            {treeData.length ? (
              <Tree
                treeData={treeData}
                defaultExpandAll
                blockNode
                onSelect={(keys) => {
                  const k = keys[0];
                  if (typeof k === 'string' && k) addField(k);
                }}
              />
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>先在上方粘贴/拉取一条样例报文</Text>
            )}
          </div>
        </Col>

        <Col span={17}>
          <Space style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: 12 }}>① 字段映射</Text>
            <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => setMap([...mapFields, { from: '', to: '', type: 'number' }])} data-testid="vt-add-field">
              添加字段
            </Button>
          </Space>
          <Table
            size="small"
            rowKey={(_, i) => String(i)}
            dataSource={mapFields}
            columns={columns}
            pagination={false}
            scroll={{ y: 200 }}
            locale={{ emptyText: '暂无字段映射（可从样例树点选或套用模板）' }}
            data-testid="vt-map-table"
          />

          <Divider style={{ margin: '10px 0' }} />
          <Space style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: 12 }}>② 过滤条件</Text>
            {filter && filterItems.length > 0 ? (
              <Segmented
                size="small"
                value={filter.logic}
                onChange={(v) => setFilter({ logic: v as 'and' | 'or', items: filterItems })}
                options={[{ value: 'and', label: '且 AND' }, { value: 'or', label: '或 OR' }]}
              />
            ) : (
              <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => setFilter({ logic: 'and', items: [{ field: fields[0] ?? '', op: 'gt', value: '' }] })} data-testid="vt-add-filter">
                添加过滤
              </Button>
            )}
          </Space>
          {filter && filterItems.length > 0 && (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {filterItems.map((it: any, i) => (
                <Space key={i} wrap size={4}>
                  <Select
                    size="small"
                    style={{ width: 120 }}
                    value={it.field}
                    onChange={(v) => updateFilterItem(i, { field: v })}
                    options={fields.map((f) => ({ value: f, label: f }))}
                  />
                  <Select
                    size="small"
                    style={{ width: 110 }}
                    value={it.op}
                    onChange={(v) => updateFilterItem(i, { op: v })}
                    options={[
                      { value: 'eq', label: '等于' },
                      { value: 'neq', label: '不等于' },
                      { value: 'gt', label: '大于' },
                      { value: 'gte', label: '大于等于' },
                      { value: 'lt', label: '小于' },
                      { value: 'lte', label: '小于等于' },
                      { value: 'contains', label: '包含' },
                      { value: 'in', label: '属于' },
                      { value: 'exists', label: '存在' },
                    ]}
                  />
                  {it.op !== 'exists' && (
                    <Input
                      size="small"
                      style={{ width: 160 }}
                      value={Array.isArray(it.value) ? it.value.join(', ') : it.value === undefined ? '' : String(it.value)}
                      onChange={(e) =>
                        updateFilterItem(i, {
                          value: it.op === 'in'
                            ? e.target.value.split(',').map((s) => parseScalar(s)).filter((v) => v !== undefined)
                            : parseScalar(e.target.value),
                        })
                      }
                      placeholder={it.op === 'in' ? '多个值用逗号分隔' : '值'}
                    />
                  )}
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => setFilter({ logic: filter.logic, items: filterItems.filter((_, x) => x !== i) })} />
                </Space>
              ))}
              <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => setFilter({ logic: filter.logic, items: [...filterItems, { field: fields[0] ?? '', op: 'gt', value: '' }] })}>
                添加条件
              </Button>
            </Space>
          )}

          <Divider style={{ margin: '10px 0' }} />
          <Space style={{ marginBottom: 4 }}>
            <Text strong style={{ fontSize: 12 }}>③ 计算字段</Text>
            <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => setCompute([...computeFields, { to: '', expr: '', type: 'number' }])} data-testid="vt-add-compute">
              添加计算
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>可用函数：round/if/coalesce/cToF/fToC/min/max/abs/…</Text>
          </Space>
          {computeFields.length > 0 && (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {computeFields.map((c, i) => {
                const err = c.expr ? validateExpressionText(c.expr, fields) : null;
                return (
                  <div key={i}>
                    <Space wrap size={4}>
                      <Input size="small" style={{ width: 110 }} value={c.to} placeholder="字段名" data-testid="vt-compute-to" onChange={(e) => setCompute(computeFields.map((x, y) => (y === i ? { ...x, to: e.target.value } : x)))} />
                      <span style={{ fontSize: 12 }}>=</span>
                      <Input
                        size="small"
                        style={{ width: 240 }}
                        value={c.expr}
                        placeholder="如 cToF(temp) 或 value * 0.1"
                        data-testid="vt-compute-expr"
                        status={err ? 'error' : undefined}
                        onChange={(e) => setCompute(computeFields.map((x, y) => (y === i ? { ...x, expr: e.target.value } : x)))}
                      />
                      <Select
                        size="small"
                        style={{ width: 90 }}
                        value={c.type ?? 'number'}
                        onChange={(v) => setCompute(computeFields.map((x, y) => (y === i ? { ...x, type: v } : x)))}
                        data-testid="vt-compute-type"
                        options={[
                          { value: 'number', label: '数字' },
                          { value: 'string', label: '文本' },
                          { value: 'boolean', label: '布尔' },
                          { value: 'date', label: '日期' },
                        ]}
                      />
                      <InputNumber size="small" min={0} max={6} value={c.decimals} placeholder="小数" style={{ width: 70 }} onChange={(v) => setCompute(computeFields.map((x, y) => (y === i ? { ...x, decimals: v === null ? undefined : v } : x)))} />
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => setCompute(computeFields.filter((_, x) => x !== i))} />
                    </Space>
                    {err && <Text type="danger" style={{ fontSize: 12, marginInlineStart: 120 }}>{err}</Text>}
                  </div>
                );
              })}
            </Space>
          )}

          {showOutput && (
            <>
              <Divider style={{ margin: '10px 0' }} />
              <Text strong style={{ fontSize: 12 }}>④ 输出路由</Text>
              <div style={{ marginTop: 4 }}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Space>
                    <Text style={{ width: 70, fontSize: 12 }}>points</Text>
                    <Select
                      size="small"
                      mode="multiple"
                      allowClear
                      style={{ minWidth: 360 }}
                      placeholder="默认=全部字段（业务点位，大屏/3D 绑定）"
                      value={outputValue?.points ?? []}
                      onChange={(v) => setOutput('points', v)}
                      data-testid="vt-output-points"
                      options={fields.map((f) => ({ value: f, label: f }))}
                    />
                  </Space>
                  <Space>
                    <Text style={{ width: 70, fontSize: 12 }}>io</Text>
                    <Select
                      size="small"
                      mode="multiple"
                      allowClear
                      style={{ minWidth: 360 }}
                      placeholder="IO 点位（如 ai0；不选=保留内置 io）"
                      value={outputValue?.io ?? []}
                      onChange={(v) => setOutput('io', v)}
                      options={fields.map((f) => ({ value: f, label: f }))}
                    />
                  </Space>
                  <Space>
                    <Text style={{ width: 70, fontSize: 12 }}>network</Text>
                    <Select
                      size="small"
                      mode="multiple"
                      allowClear
                      style={{ minWidth: 360 }}
                      placeholder="网络状态字段（不选=保留内置 network）"
                      value={outputValue?.network ?? []}
                      onChange={(v) => setOutput('network', v)}
                      options={fields.map((f) => ({ value: f, label: f }))}
                    />
                  </Space>
                </Space>
              </div>
            </>
          )}

          {!mapFields.length && (
            <Alert
              style={{ marginTop: 10 }}
              type="info"
              showIcon
              message="先套用模板或点样例节点添加字段；保存前建议点「解析」看结果"
            />
          )}
        </Col>
      </Row>

      <Modal
        title={
          <span>
            AI 生成解析（需求 → 规格）
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleOutlined />}
              onClick={() => setAiHelpOpen(true)}
              data-testid="vt-ai-help"
              title="查看如何写需求（示例）"
              style={{ marginLeft: 4 }}
            />
          </span>
        }
        open={aiOpen}
        onCancel={() => setAiOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setAiOpen(false)}>取消</Button>,
          <Button key="gen" loading={aiLoading} onClick={runAiGenerate} data-testid="vt-ai-generate">
            生成 / 重新生成
          </Button>,
          <Button
            key="apply"
            type="primary"
            disabled={!aiResult?.ok || (aiResult?.product === 'code' ? !aiResult?.script : !aiResult?.spec)}
            onClick={applyAi}
            data-testid="vt-ai-apply"
          >
            采用
          </Button>,
        ]}
        data-testid="vt-ai-modal"
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          描述对数据的要求（字段/单位/过滤/分级/输出点位）。会消耗一次 AI 配额；自动修复不重复计。
        </Text>
        {mode === 'object' && onApplyCode && (
          <div style={{ marginTop: 6 }}>
            <Segmented
              size="small"
              value={aiProduct}
              onChange={(v) => setAiProduct(v as 'spec' | 'code')}
              options={[
                { value: 'spec', label: '可视化规格（推荐）' },
                { value: 'code', label: '代码（高级）' },
              ]}
              data-testid="vt-ai-product"
            />
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
              {aiProduct === 'code'
                ? '仅用于可视化表达不了的场景（二进制/窗口/有状态）'
                : '生成可审查的规格，采用后可在可视化编辑器修改'}
            </Text>
          </div>
        )}
        <Input.TextArea
          rows={4}
          value={aiNotes}
          onChange={(e) => setAiNotes(e.target.value)}
          placeholder="如：温度单位 0.1℃，湿度 %；温度 > 30 输出 level=high，否则 low；输出 temp/humidity/level"
          style={{ marginTop: 8 }}
          data-testid="vt-ai-requirements"
        />
        <Space style={{ marginTop: 8 }} wrap>
          <Input
            value={aiTargets}
            onChange={(e) => setAiTargets(e.target.value)}
            placeholder="必须输出的字段（逗号分隔，可选）"
            style={{ width: 300 }}
            data-testid="vt-ai-targets"
          />
          <Select
            value={aiProtocol}
            onChange={setAiProtocol}
            style={{ width: 160 }}
            data-testid="vt-ai-protocol"
            options={[
              { value: 'auto', label: '协议：自动' },
              { value: 'rtu-v1', label: 'RTU v1.0' },
              { value: 'rtu-v2', label: 'RTU v2.0' },
              { value: 'modbus', label: 'Modbus' },
              { value: 'json', label: '扁平 JSON' },
            ]}
          />
        </Space>
        {aiError && <Alert type="error" showIcon message={aiError} style={{ marginTop: 8 }} data-testid="vt-ai-error" />}
        {aiResult && (
          <div style={{ marginTop: 10 }} data-testid="vt-ai-result">
            <Alert
              type={aiResult.ok ? 'success' : 'warning'}
              showIcon
              message={
                aiResult.ok
                  ? `生成成功（尝试 ${aiResult.attempts ?? 1} 次${aiResult.model ? `，模型 ${aiResult.model}` : ''}）`
                  : `未生成可用规格（尝试 ${aiResult.attempts ?? 1} 次）`
              }
            />
            {aiResult.product === 'code' && aiResult.script && (
              <pre
                style={{
                  fontSize: 12,
                  maxHeight: 200,
                  overflow: 'auto',
                  background: 'rgba(255,255,255,0.06)',
                  color: '#e6e6e6',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 4,
                  padding: 8,
                  marginTop: 6,
                }}
                data-testid="vt-ai-code"
              >
                {aiResult.script}
              </pre>
            )}
            {aiResult.explanation && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }} data-testid="vt-ai-explanation">
                {aiResult.explanation}
              </Text>
            )}
            {!!aiResult.warnings?.length && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 6 }}
                message={aiResult.warnings.join('；')}
                data-testid="vt-ai-warnings"
              />
            )}
            {!!aiResult.testResults?.length && (
              <div style={{ marginTop: 6 }} data-testid="vt-ai-tests">
                <Table
                  size="small"
                  rowKey={(_, i) => String(i)}
                  pagination={false}
                  dataSource={aiResult.testResults}
                  columns={[
                    { title: '样例', dataIndex: 'name', width: 90 },
                    {
                      title: '结果',
                      dataIndex: 'ok',
                      width: 90,
                      render: (v: boolean) => (v ? <Tag color="green">通过</Tag> : <Tag color="red">失败</Tag>),
                    },
                    { title: '错误', dataIndex: 'error', ellipsis: true },
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      <AiRequirementHelpModal
        open={aiHelpOpen}
        onClose={() => setAiHelpOpen(false)}
        mode={mode}
        onUse={(ex) => {
          setAiNotes(ex.requirements);
          setAiTargets(ex.targetFields.join(', '));
          setAiProtocol(ex.protocol ?? 'auto');
          setAiError('');
          setAiResult(null);
          message.success('示例已填入，可修改后点「生成」');
        }}
      />
    </div>
  );
}
